import { subscribeAccessToken } from "./api.js";

/**
 * Page-session cache for GET responses that more than one view reads. Entries
 * expire after `TTL_MS`, a request already in flight for a key is shared by every
 * caller, and everything is dropped when the session ends (logout or expiry), so
 * one viewer's data never shows up for the next sign-in.
 */
const TTL_MS = 60_000;

interface Entry { at: number; value: unknown }

const entries = new Map<string, Entry>();
const inFlight = new Map<string, Promise<unknown>>();

/** The cached value for `key`, or undefined when it is missing or expired. */
export function getCached<T>(key: string): T | undefined {
  const hit = entries.get(key);
  if (!hit) return undefined;
  if (Date.now() - hit.at > TTL_MS) { entries.delete(key); return undefined; }
  return hit.value as T;
}

/** Drops every entry, and every request in flight, whose key starts with `prefix`. */
export function invalidateCached(prefix: string): void {
  for (const key of entries.keys()) if (key.startsWith(prefix)) entries.delete(key);
  for (const key of inFlight.keys()) if (key.startsWith(prefix)) inFlight.delete(key);
}

/**
 * Resolves with the cached value for `key` when there is a fresh one. Otherwise runs
 * `load` once (concurrent callers share it) and caches the result if `store` accepts it.
 */
export function fetchCached<T>(key: string, load: () => Promise<T>, store: (value: T) => boolean = () => true): Promise<T> {
  const hit = getCached<T>(key);
  if (hit !== undefined) return Promise.resolve(hit);
  const pending = inFlight.get(key);
  if (pending) return pending as Promise<T>;
  // A response that lands after its key was invalidated is not stored: the
  // invalidation happened because the data changed while the request was running.
  const request: Promise<T> = load()
    .then((value) => {
      if (inFlight.get(key) === request && store(value)) entries.set(key, { at: Date.now(), value });
      return value;
    })
    .finally(() => { if (inFlight.get(key) === request) inFlight.delete(key); });
  inFlight.set(key, request);
  return request;
}

subscribeAccessToken((token) => {
  if (token) return;
  entries.clear();
  inFlight.clear();
});
